import { useBreakpointValue } from '@stockx/ui';
import { useMemo } from 'react';

interface Props {
  src: string | undefined;
  disableSrcSets?: boolean;
  options?: any;
}

interface ReturnProps {
  imageSrc: string;
  imageSrcSet: string | undefined;
}

// 1x-5x srcSet constants
const TARGET_SIZE_VALUES = [
  { zoom: '1x', dpr: '1', quality: '80' },
  { zoom: '2x', dpr: '2', quality: '75' },
  { zoom: '3x', dpr: '3', quality: '50' },
  { zoom: '4x', dpr: '4', quality: '45' },
  { zoom: '5x', dpr: '5', quality: '40' },
];

// Creates individual image src string
const createImgSrc = (
  src: string,
  isMobile: boolean,
  options: Record<string, any> = {},
) => {
  const parts: string[] = src.split('?');
  const searchParams = new URLSearchParams(parts[1] ?? '');

  const query = {
    quality: isMobile ? '50' : '75',
    auto: 'webp',
    format: 'pjpg',
    ...(!options?.dpr ? { dpr: isMobile ? '3' : '2' } : {}),
    ...Object.fromEntries(searchParams.entries()),
    ...options,
  };

  return `${parts[0]}?${decodeURIComponent(
    new URLSearchParams(query).toString(),
  )}`;
};

// Builds comma-delimited srcSet string using TARGET_SIZE_VALUES constants
const buildResponsiveSrcSet = (src, isMobile, options = {}) =>
  TARGET_SIZE_VALUES.map((targetValue) => {
    const imgSrc = createImgSrc(src, isMobile, {
      quality: targetValue.quality,
      dpr: targetValue.dpr,
      ...options,
    });

    return `${imgSrc} ${targetValue.zoom}`;
  }).join(', ');

/**
 * Build for Contentstack image src sets.
 *
 * @param src
 * @param preferredImageFormat
 * @param disableSrcSets
 * @param options
 */
const useBuildContentstackImageSrc = ({
  src = '',
  disableSrcSets = false,
  options = {},
}: Props): ReturnProps => {
  const { isMobile } = useBreakpointValue();
  return useMemo(
    () => ({
      imageSrc: createImgSrc(src, isMobile, options),
      imageSrcSet: !disableSrcSets
        ? buildResponsiveSrcSet(src, isMobile, options)
        : undefined,
    }),
    [src, isMobile, disableSrcSets],
  );
};

export default useBuildContentstackImageSrc;
